import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import { t } from 'i18next';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';

import NumberInput from '~components/FormFields/v2/NumberInput';
import TextInput from '~components/FormFields/v2/TextInput';
import { PaymentTerm } from '~hooks/usePaymentTerms/models';

import {
  PaymentTermDto,
  PaymentTermDtoAsyncError,
  paymentTermSchema,
  setPaymentTermSchemaDefaultValues,
} from './paymentTermSchema';

export type PaymentTermFormHandler = {
  onSubmit: () => Promise<PaymentTermDto | undefined>;
};

interface PaymentTermFormProps {
  asyncErrors?: PaymentTermDtoAsyncError[];
  paymentTerm?: PaymentTerm;
}

const PaymentTermForm = forwardRef<PaymentTermFormHandler, PaymentTermFormProps>(
  function PaymentTermForm({ asyncErrors, paymentTerm }, ref) {
    const { control, handleSubmit, setError, clearErrors } = useForm<PaymentTermDto>({
      resolver: yupResolver(paymentTermSchema),
      mode: 'onSubmit',
      reValidateMode: 'onChange',
      defaultValues: setPaymentTermSchemaDefaultValues(paymentTerm),
    });

    useImperativeHandle(
      ref,
      () => ({
        onSubmit() {
          return new Promise((resolve) => {
            handleSubmit(
              (data) => {
                resolve(data);
              },
              () => {
                resolve(undefined);
              },
            )();
          });
        },
      }),
      [handleSubmit],
    );

    useEffect(() => {
      if (asyncErrors) {
        for (const error of asyncErrors) {
          setError(error.field, { type: 'manual', message: error.message });
        }
      }

      return () => {
        clearErrors();
      };
    }, [asyncErrors, clearErrors, setError]);

    return (
      <Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 3,
          }}
        >
          <TextInput
            control={control}
            isRequired
            label={t('form_fields.name')}
            name="name"
          />

          <NumberInput
            control={control}
            slotProps={{ htmlInput: { min: 0 } }}
            isRequired
            label={t('administration.payment_terms.due_days')}
            name="dueDays"
          />
        </Box>
      </Box>
    );
  },
);

export default PaymentTermForm;
